import { useCallback, useMemo, VFC } from 'react';

import FieldSchemaSelect from 'dashboard/components/FieldSchemaSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { generateJsonSchemaConfig, UsableObjectConfig } from 'dashboard/lib/workflows';
import { WorkflowError } from 'dashboard/models/workflow';
import {
  WorkflowStep,
  WorkflowStepLoop,
  WorkflowStepLoopMapSettings,
} from 'dashboard/models/workflow-version';
import { useSchemaForFieldPath } from 'dashboard/redux/selectors/workflow-editor-context';
import { JsonSchemaArray } from 'lib/json-schema';

interface StepConfigMapOutputProps {
  usableObjects: UsableObjectConfig[];
  loopStep: WorkflowStepLoop;
  editable: boolean;
  errors: WorkflowError[];
  updateStep: (newStep: WorkflowStep) => void;
}

const StepConfigMapOutput: VFC<StepConfigMapOutputProps> = ({
  editable,
  usableObjects,
  loopStep,
  errors,
  updateStep,
}) => {
  const schemaForFieldPath = useSchemaForFieldPath();
  const { jsonSchema } = useMemo(() => {
    return generateJsonSchemaConfig({
      usableObjects: usableObjects,
      useOriginal: true,
    });
  }, [usableObjects]);
  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError<'item-path'>(errors);
  const settings = loopStep.settings as WorkflowStepLoopMapSettings;
  const updateItemPath = useCallback(
    (itemPath: string) => {
      updateStep({
        ...loopStep,
        settings: {
          ...settings,
          'item-path': itemPath,
          'output-item-schema': schemaForFieldPath(itemPath, jsonSchema).schema as JsonSchemaArray,
        },
      });
    },
    [jsonSchema, loopStep, schemaForFieldPath, settings, updateStep],
  );

  return (
    <>
      <ParamLabel labelText='Result' htmlFor='step-config-loop__item-path-input' />
      <FieldSchemaSelect
        id='step-config-loop__item-path'
        disabled={!editable}
        initialValue={settings['item-path']}
        usableObjects={usableObjects}
        onFieldSelect={({ value }) => updateItemPath(value)}
        size='sm'
        hasError={hasErrorFor('item-path')}
        aria-invalid={hasErrorFor('item-path')}
      />
      {renderErrorMessageFor('item-path')}
    </>
  );
};

export default StepConfigMapOutput;
